import React, { createContext, useContext, useEffect, useRef, useState, } from "react";
import { C3UserConfigurationContext } from "../c3-user-configuration-provider";
import { getUserTheme, updateTheme, getClusters, getOrgs, } from "../../../api/profile";
import { CarbonThemeProvider, resolveTheme, } from "./carbon-theme-provider";
import { useApi } from "../../../hooks/useApi";
export const defaultTheme = "light";
export const C3ProfileContext = createContext({
    isEnabled: false,
    theme: defaultTheme,
    orgs: null,
    activeOrg: null,
    reloadOrgs: () => undefined,
    removeOrg: () => undefined,
    clusters: null,
    reloadClusters: () => undefined,
    resolvedTheme: defaultTheme,
    onThemeChange: () => undefined,
    loadClustersById: () => undefined,
    setClusters: () => undefined,
});
export const C3ProfileProvider = ({ children }) => {
    const { decodedAudience, analyticsTrack, ...config } = useContext(C3UserConfigurationContext);
    const isEnabled = !!config;
    const themeRef = useRef(getUserTheme(config.userToken) || defaultTheme);
    const [resolvedTheme, setResolvedTheme] = useState(defaultTheme);
    const isConfigValid = !!(config.userToken &&
        config.activeOrganizationId &&
        decodedAudience);
    const { data: orgs, setData: setOrgs, refetch: loadOrgs, } = useApi({
        queryFn: () => {
            return getOrgs(decodedAudience ?? "", config.userToken).then((response) => {
                return {
                    ...response,
                    data: response.result?.sort(({ name: a }, { name: b }) => a.localeCompare(b)),
                };
            });
        },
        queryKey: [decodedAudience, config.userToken],
        enabled: isConfigValid,
    });
    const { data: clusters, setData: setClusters, refetch: loadClusters, } = useApi({
        queryFn: () => {
            if (!isConfigValid)
                return;
            return getClusters(decodedAudience ?? "", config.userToken, config.activeOrganizationId);
        },
        queryKey: [decodedAudience, config.userToken, config.activeOrganizationId],
        enabled: isConfigValid,
    });
    const activeOrg = orgs?.find((org) => org.uuid === config.activeOrganizationId) ?? null;
    const loadClustersById = (orgId) => {
        if (!isConfigValid)
            return;
        return getClusters(decodedAudience, config.userToken, orgId);
    };
    const removeOrg = (orgId) => {
        if (orgs?.length === 1)
            return;
        setOrgs((orgs) => orgs?.filter((org) => org.uuid !== orgId) || null);
    };
    useEffect(() => {
        const updateSystemTheme = ({ matches }) => {
            if (themeRef.current === "system")
                setResolvedTheme(matches ? "dark" : "light");
        };
        window
            .matchMedia("(prefers-color-scheme: dark)")
            .addEventListener("change", updateSystemTheme);
        return () => {
            window
                .matchMedia("(prefers-color-scheme: dark)")
                .removeEventListener("change", updateSystemTheme);
        };
    }, []);
    useEffect(() => {
        if (isEnabled && config.userToken && config.handleTheme) {
            const currentTheme = getUserTheme(config.userToken);
            if (currentTheme) {
                themeRef.current = currentTheme;
                setResolvedTheme(resolveTheme(currentTheme));
            }
        }
    }, [config?.userToken]);
    const onThemeChange = (newTheme) => {
        if (isEnabled) {
            themeRef.current = newTheme;
            setResolvedTheme(resolveTheme(newTheme));
            updateTheme({ ...config, theme: newTheme });
            analyticsTrack?.("theme:update", { value: newTheme });
        }
    };
    if (!isEnabled)
        return children;
    const content = config.handleTheme ? (React.createElement(CarbonThemeProvider, null, children)) : (children);
    return (React.createElement(C3ProfileContext.Provider, { value: {
            isEnabled,
            theme: themeRef.current,
            resolvedTheme,
            orgs,
            activeOrg,
            reloadOrgs: loadOrgs,
            clusters,
            setClusters,
            reloadClusters: loadClusters,
            removeOrg,
            onThemeChange,
            loadClustersById,
        } }, content));
};
export const useC3Profile = () => useContext(C3ProfileContext);
